import { AVATAR_COLORS, inDrawOrder } from "@clubap/shared";
import type { CSSProperties } from "react";
import { AVATAR_ART, ITEM_ART, placementFor, type DirectionalFrames } from "../art/manifest.js";

/** El entero del catálogo, como lo escribe el CSS. */
function hex(color: number): string {
  return "#" + color.toString(16).padStart(6, "0");
}

/**
 * Tu tortuga, quieta y de frente, con lo que lleve puesto.
 *
 * Existe porque la tienda y el inventario eran listas de texto: se compraba y se
 * equipaba a ciegas, y en un juego donde lo único que se compra es aspecto, eso deja
 * la decisión sin la información que la motiva. El dibujo de cada ítem ya existía —el
 * motor lo pinta sobre el avatar en el mundo— pero no llegaba al panel donde se decide.
 *
 * Son `img` y no Pixi a propósito. El reparto del proyecto es mundo en Pixi, UI en DOM,
 * y montar un canvas por miniatura para dibujar dos PNG superpuestos sería pagar un
 * contexto de WebGL por cada fila de la lista.
 */

/** El fotograma que mira al frente. Las capas declaran `s`, o un string suelto. */
function frontFrame(frames: DirectionalFrames): string | null {
  if (typeof frames === "string") return frames;
  if (Array.isArray(frames)) return frames[0] ?? null;

  const south = frames.s;
  if (typeof south === "string") return south;
  return south?.[0] ?? null;
}

interface Props {
  /** Ids de ítem puestos. Se ordenan por ranura, igual que en el mundo. */
  worn: readonly string[];
  /**
   * ÍNDICE en `AVATAR_COLORS`. Sin él la figura sale sin teñir.
   *
   * Es índice y no color por lo mismo que en la base y en el cable: la lista es
   * append-only y lo que identifica a un color es su posición.
   */
  color?: number;
  /**
   * Qué parte se enseña. Por defecto la figura entera.
   *
   * "cabeza" no contradice lo de abajo: aquella miniatura ALEJABA la figura hasta
   * dejar el ítem en siete píxeles, y este encuadre es una ventana que ACERCA. El
   * recorte vive en el CSS de `.retrato`, con las medidas y de dónde salen.
   */
  encuadre?: "cuerpo" | "cabeza";
}

/*
 * Sin versión miniatura para las filas de la lista, a propósito.
 *
 * Los dibujos de ítem se exportan sobre el lienzo del avatar —un sombrero ocupa la
 * franja de arriba de 400x512— así que una miniatura del lienzo entero deja el ítem
 * en siete píxeles: un adorno que no informa. Recortar a la caja de cada dibujo
 * pediría una bbox por ítem en el manifest, y eso es un dato nuevo que hoy nadie
 * necesita. La figura grande es la que enseña.
 */
export function AvatarPreview({ worn, color, encuadre = "cuerpo" }: Props) {
  const layers = AVATAR_ART?.layers ?? [];
  const tinte = color === undefined ? null : hex(AVATAR_COLORS[color] ?? AVATAR_COLORS[0]);

  const figura = (
    <div className="figura">
      {/*
        Sin arte de avatar declarado el mundo dibuja primitivas, pero acá no hay nada
        que enseñar: se dice, en vez de mostrar un hueco que parece un fallo de carga.
      */}
      {layers.length === 0 && <span className="figura__falta">sin dibujo</span>}

      {layers.map((layer, index) => {
        const src = frontFrame(layer.idle);
        if (!src) return null;

        const img = <img src={src} alt="" className="figura__capa" />;

        /*
         * Las capas marcadas `tinted` se tiñen acá igual que en el mundo.
         *
         * En Pixi es `sprite.tint`, que multiplica el color sobre el dibujo. El
         * equivalente en DOM es pintar el color encima con `mix-blend-mode: multiply`
         * y recortarlo con el alfa del propio PNG por máscara — sin la máscara el
         * color se saldría por el rectángulo de la imagen. Es la misma operación, así
         * que el probador enseña el mismo caparazón que van a ver los demás.
         */
        if (!layer.tinted || !tinte) return <span key={`capa-${index}`}>{img}</span>;

        return (
          <span
            key={`capa-${index}`}
            className="figura__tenida"
            style={{ "--tinte": tinte, "--sprite": `url(${src})` } as CSSProperties}
          >
            {img}
          </span>
        );
      })}

      {/*
        El MISMO orden de dibujo que en el mundo (`inDrawOrder`), y por la misma razón:
        es lo que decide qué tapa a qué. Un ítem sin arte declarado no se ve, igual que
        en el juego — la economía funciona, sólo falta el dibujo.
      */}
      {inDrawOrder(worn).map((item) => {
        const art = ITEM_ART[item.id];
        const src = art && frontFrame(art.idle);
        if (!src) return null;

        /*
         * La figura mira al frente, así que la colocación es la de `s` y nunca hay
         * espejo. Es la MISMA función que usa el mundo, a propósito: calcularlo aparte
         * dejaría la tienda enseñando el sombrero en un sitio y el campus en otro.
         */
        const place = placementFor(art.placement, "s");

        return (
          <img
            key={item.id}
            src={src}
            alt=""
            className="figura__capa"
            style={{
              /*
               * El desplazamiento va en unidades del lienzo (400x512), así que acá se
               * convierte a porcentaje del mismo lienzo. El origen es abajo-centro
               * —`transform-origin` en el CSS— para que escalar crezca hacia arriba
               * desde los pies, igual que el `anchor(0.5, 1)` de Pixi.
               */
              transform: `translate(${(place.x / 400) * 100}%, ${(place.y / 512) * 100}%) scale(${place.scale})`,
              zIndex: place.z,
            }}
          />
        );
      })}
    </div>
  );

  return encuadre === "cabeza" ? <div className="retrato">{figura}</div> : figura;
}
